import { useEffect, useState, useRef } from 'react';
import { futebolRepository } from '../../data/futebolRepository';
import type { FutebolPartidaAtiva, FutebolJogoDia, FutebolBolaoPalpite } from '@/shared/types';
import { soundEngine } from '@/shared/lib/soundEngine';

interface Props {
  telaId: string;
  partida: FutebolPartidaAtiva | null;
  jogo: FutebolJogoDia | null;
}

interface ResultadoFinal {
  fixtureId:       number;
  timeCasa:        string;
  timeVisitante:   string;
  placarCasa:      number;
  placarVisitante: number;
}

export default function TvBolaoResultadoPopup({ telaId, partida, jogo }: Props) {
  // Retrato do placar no apito: logo depois do FT a sync zera fixture_id da
  // tela (e useFutebolRealtime zera `jogo`), então o popup não pode depender
  // de partida/jogo ao vivo durante os 30 s em que fica aberto.
  const [resultado, setResultado] = useState<ResultadoFinal | null>(null);
  const [loading, setLoading] = useState(false);
  const [vencedores, setVencedores] = useState<FutebolBolaoPalpite[]>([]);

  // Persiste o fixture_id cujo popup já foi exibido — sobrevive a remounts em TV Box 24/7
  const triggeredFixtureRef = useRef<number | null>(null);
  // Timeout de auto-fechamento (30s) — rastreado para limpar no unmount/troca de jogo.
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!partida || !telaId) return;

    // Outro jogo entrou no ar: fecha o resultado anterior.
    if (partida.fixture_id && partida.tempo_jogo !== 'FT') {
      setResultado(prev => (prev && prev.fixtureId !== partida.fixture_id ? null : prev));
    }

    if (!jogo) return;

    if (partida.tempo_jogo === 'FT' && partida.fixture_id) {
      const storageKey = `ds:bolao_resultado_${partida.fixture_id}`;
      const alreadyTriggered =
        triggeredFixtureRef.current === partida.fixture_id ||
        localStorage.getItem(storageKey) === '1';

      if (!alreadyTriggered) {
        triggeredFixtureRef.current = partida.fixture_id;
        try { localStorage.setItem(storageKey, '1'); } catch { /* quota */ }

        soundEngine.playSound('bolao_resultado');
        setLoading(true);
        setVencedores([]);
        setResultado({
          fixtureId:       partida.fixture_id,
          timeCasa:        jogo.time_casa,
          timeVisitante:   jogo.time_visitante,
          placarCasa:      partida.placar_casa,
          placarVisitante: partida.placar_visitante,
        });

        futebolRepository
          .buscarVencedoresBolao(
            telaId,
            partida.fixture_id,
            partida.placar_casa,
            partida.placar_visitante
          )
          .then((ganhadores) => {
            setVencedores(ganhadores);
          })
          .catch((err) => console.error('[Bolao] Erro ao buscar vencedores:', err))
          .finally(() => {
            setLoading(false);
            // Fecha o popup após 30 segundos
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = setTimeout(() => {
              setResultado(null);
            }, 30000);
          });
      }
    }
  }, [partida?.tempo_jogo, partida?.fixture_id, partida?.placar_casa, partida?.placar_visitante, telaId, jogo]);

  // Limpa o timeout pendente ao desmontar (TV Box 24/7).
  useEffect(() => () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  }, []);

  if (!resultado) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md animate-in fade-in duration-700">
      <div className="bg-gradient-to-b from-gray-900 to-black border border-amber-500/30 p-12 rounded-3xl shadow-[0_0_80px_rgba(245,158,11,0.2)] max-w-4xl w-full text-center relative overflow-hidden flex flex-col items-center">
        
        {/* Confetes / Brilhos no fundo */}
        <div className="absolute inset-0 pointer-events-none opacity-40 mix-blend-screen bg-[radial-gradient(ellipse_at_center,_rgba(245,158,11,0.4)_0%,_rgba(0,0,0,0)_70%)]" />

        <div className="text-8xl mb-6 drop-shadow-[0_0_20px_rgba(245,158,11,0.8)]">🏆</div>
        
        <h2 className="text-4xl font-black text-white uppercase tracking-widest mb-2">
          Resultado do Bolão
        </h2>
        <p className="text-amber-400 font-bold text-xl mb-10 tracking-widest uppercase">
          Placar Final: {resultado.timeCasa} {resultado.placarCasa} × {resultado.placarVisitante} {resultado.timeVisitante}
        </p>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-white/60 font-bold tracking-widest uppercase">Apurando ganhadores...</p>
          </div>
        ) : vencedores.length > 0 ? (
          <div className="flex flex-col w-full px-8">
            <h3 className="text-emerald-400 font-black text-3xl mb-8 uppercase tracking-widest drop-shadow-[0_0_10px_rgba(16,185,129,0.5)]">
              🎉 Parabéns aos Vencedores! 🎉
            </h3>
            
            <div className="flex flex-wrap justify-center gap-4">
              {vencedores.map((v) => (
                <div key={v.id} className="bg-white/10 border border-amber-500/50 rounded-2xl px-8 py-4 flex items-center justify-center shadow-lg">
                  <span className="text-white font-black text-3xl">{v.identificacao}</span>
                </div>
              ))}
            </div>
            <p className="text-white/40 mt-12 text-sm">Dirijam-se ao caixa para resgatar os prêmios!</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="text-6xl mb-4 opacity-70">😢</div>
            <h3 className="text-white/80 font-bold text-2xl mb-2">Ninguém acertou...</h3>
            <p className="text-white/40">Fica para o próximo jogo!</p>
          </div>
        )}
      </div>
    </div>
  );
}
